import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUiStore } from '@/store/uiStore.ts';
import { getRecordingMimeType } from '@/audio/audioConstants.ts';
import { capabilities } from '@/capabilities.ts';
import { sendToNative } from '@/bridge/embeddedBridge.ts';
import { useAudioStore } from '@/store/audioStore.ts';
import styles from './AudioWorkspace.module.css';

export interface RecordResult {
  rawData: Uint8Array;
  audioBuffer: AudioBuffer | null;  // null in embedded mode
  peaks: Float32Array;
  duration: number;
  mimeType: string;
  clipId?: number;  // Set only in embedded mode — clip already in Unity's SQLite
}

interface Props {
  onStage: (result: RecordResult, label: string) => void;
  onBusyChange?: (busy: boolean) => void;
}

export function RecordPanel({ onStage, onBusyChange }: Props) {
  const { t } = useTranslation('audioWorkspace');
  const [countdown, setCountdown] = useState<number | null>(null);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef(0);
  const setIsRecording = useUiStore(s => s.setIsRecording);

  const busy = recording || countdown !== null;
  useEffect(() => { onBusyChange?.(busy); }, [busy, onBusyChange]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current?.state !== 'inactive') {
        mediaRecorderRef.current?.stop();
      }
    };
  }, []);

  // Stage a completed embedded recording — shared by the live __recordingComplete
  // listener below and the pending-clip pickup on mount (audioStore fallback for a
  // clip that arrived via bridge while this panel was unmounted).
  const stageClip = useCallback((detail: { clipId: number; duration: number; peaks: Float32Array; label: string }) => {
    const { clipId, duration, peaks, label } = detail;
    setRecording(false);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setElapsed(0);
    setIsRecording(false, null);
    onStage({
      clipId,
      duration,
      peaks,
      rawData: new Uint8Array(0),
      audioBuffer: null,
      mimeType: 'audio/wav',
    }, label);
  }, [onStage, setIsRecording]);

  // Pick up a clip that arrived via bridge while this panel was unmounted
  // (e.g. Unity's recordingComplete fired while AudioWorkspace was closed).
  useEffect(() => {
    if (!capabilities.embeddedRecording) return;
    const pending = useAudioStore.getState().pendingRecordedClip;
    if (pending) {
      stageClip(pending);
      useAudioStore.getState().clearPendingRecordedClip();
    }
  }, [stageClip]);

  // Listen for Unity recording result events (embedded mode)
  useEffect(() => {
    if (!capabilities.embeddedRecording) return;

    const handleComplete = (e: Event) => {
      stageClip((e as CustomEvent).detail);
      useAudioStore.getState().clearPendingRecordedClip();
    };

    const handleFailed = (e: Event) => {
      const { error } = (e as CustomEvent).detail;
      setRecording(false);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setElapsed(0);
      setIsRecording(false, null);
      console.error('[RecordPanel] Unity recording failed:', error);
    };

    window.addEventListener('__recordingComplete', handleComplete);
    window.addEventListener('__recordingFailed', handleFailed);
    return () => {
      window.removeEventListener('__recordingComplete', handleComplete);
      window.removeEventListener('__recordingFailed', handleFailed);
    };
  }, [stageClip, setIsRecording]);

  const handleStart = useCallback(async () => {
    try {
      if (capabilities.embeddedRecording) {
        // 3-2-1 countdown
        for (let i = 3; i >= 1; i--) {
          setCountdown(i);
          await new Promise(r => setTimeout(r, 800));
        }
        setCountdown(null);

        // Bridge path — Unity handles mic capture
        const lane = useUiStore.getState().audioWorkspaceTargetLane;
        sendToNative('startRecording', { lane });
        setRecording(true);
        setElapsed(0);
        startTimeRef.current = Date.now();
        timerRef.current = setInterval(() => {
          setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }, 250);

        // Set stop function that sends stopRecording to Unity
        setIsRecording(true, () => {
          sendToNative('stopRecording', {});
        });
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // 3-2-1 countdown
      for (let i = 3; i >= 1; i--) {
        setCountdown(i);
        await new Promise(r => setTimeout(r, 800));
      }
      setCountdown(null);

      const recMime = getRecordingMimeType();
      const mediaRecorder = new MediaRecorder(stream, recMime ? { mimeType: recMime } : undefined);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      startTimeRef.current = Date.now();

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        // Restore iOS audio session after mic recording — getUserMedia switches
        // AVAudioSession to PlayAndRecord, which silences Unity AudioSources
        if (capabilities.embeddedAudio) sendToNative('restoreAudioSession', {});
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        setRecording(false);
        setIsRecording(false, null);

        const blob = new Blob(chunksRef.current, { type: mediaRecorder.mimeType || 'audio/webm' });
        const arrayBuffer = await blob.arrayBuffer();
        const rawData = new Uint8Array(arrayBuffer);

        try {
          const { decodeAudio, generatePeaks, trimSilence, encodeAudioBufferToWav } = await import('@/audio/audioProcessor.ts');
          const rawBuffer = await decodeAudio(rawData);
          const audioBuffer = trimSilence(rawBuffer);
          const peaks = generatePeaks(audioBuffer);
          // Always store as WAV — Unity/FMOD can't decode AAC (audio/mp4) reliably
          const wavData = encodeAudioBufferToWav(audioBuffer);
          const label = `Recording ${new Date().toLocaleTimeString()}`;
          onStage({ rawData: wavData, audioBuffer, peaks, duration: audioBuffer.duration, mimeType: 'audio/wav' }, label);
        } catch (err) {
          console.error('[RecordPanel] decode failed:', err);
        }
      };

      mediaRecorder.start(100);
      setRecording(true);
      setElapsed(0);
      setIsRecording(true, () => handleStop());

      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 250);
    } catch (err) {
      console.error('[RecordPanel] mic access denied:', err);
      setCountdown(null);
    }
  }, [onStage, setIsRecording]);

  const handleStop = useCallback(() => {
    if (capabilities.embeddedRecording) {
      sendToNative('stopRecording', {});
      // Result arrives via __recordingComplete event — handled by useEffect below
      return;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;
  }, []);

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  if (countdown !== null) {
    return (
      <div className={styles.content}>
        <div className={styles.recordCenter}>
          <div className={styles.countdownOverlay}>{countdown}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.content}>
      <div className={styles.recordCenter}>
        {recording ? (
          <>
            <div className={styles.recordDuration}>{formatTime(elapsed)}</div>
            <button className={styles.stopBtn} onClick={handleStop} title={t('stopRecordingTitle')} />
          </>
        ) : (
          <>
            <button className={styles.recordBtn} onClick={handleStart} title={t('startRecordingTitle')} />
            <span className={styles.recordHint}>{t('clickToRecord')}</span>
          </>
        )}
      </div>
    </div>
  );
}
